import AbstractSocket from '@flipster/socket/AbstractSocket';
import { V2SocketEvents } from '@flipster/socket/r230522/socket';

// 서버와는 r230522 일반구독으로 통신하고, 인터페이스는 기존 r230522 고정구독으로 변환해주는 소켓입니다.
// 과도기적인 코드로, 장기적으로는 제거되어야 합니다.
export class NormalToFixedR230522Socket extends AbstractSocket<unknown, V2SocketEvents> {
  #off: (() => void) | undefined;
  #subscriptionManager: SubscriptionManager;
  #currencies: Handle;
  #symbol: Handle;
  #tickers: Handle;
  #intervalData: Handle;
  canRequestSnapshot = true;

  constructor(
    private normalR230522: AbstractSocket<unknown, V2SocketEvents>,
    public socketName = 'n2f-r230522',
  ) {
    super();
    this.#subscriptionManager = new SubscriptionManager((subscriptions) => {
      this.normalR230522.send({ s: subscriptions });
    });
    this.#currencies = this.#subscriptionManager.getHandle(['market/currency-details']);
    this.#symbol = this.#subscriptionManager.getHandle(['market/pswap-details', 'market/spot-details']);
    this.#tickers = this.#subscriptionManager.getHandle(['market/tickers', 'market/spot-tickers']);
    this.#intervalData = this.#subscriptionManager.getHandle(['market/interval-data']);
    const fixedTopics = [
      'private/account',
      'private/margins',
      'market/pswaps',
      'private/orders',
      'private/positions',
      'private/pswaps',
      'market/spots',
      'private/spot-orders',
      'private/spots',
      'private/wallets',
    ];
    this.#subscriptionManager.getHandle(fixedTopics, ['*']);
  }

  set currencies(currencies: string[] | undefined | null) {
    this.#currencies.set(currencies);
  }
  set symbol(symbol: string | undefined | null) {
    this.#symbol.set(symbol ? [symbol] : undefined);
  }
  set tickers(tickers: string | undefined | null) {
    this.#tickers.set(tickers ? [tickers] : undefined);
  }
  set intervalData(intervalData: string[] | undefined | null) {
    this.#intervalData.set(intervalData);
  }

  get state() {
    if (this.#off) return this.normalR230522.state;
    return 'closed';
  }

  connect() {
    const stateBeforeConnect = this.state;
    const offStateChanged = this.normalR230522.on('stateChanged', (state) => this.emit('stateChanged', state));
    const offConnected = this.normalR230522.on('connected', () => {
      this.normalR230522.send({ s: this.#subscriptionManager.getSubscriptions() });
    });
    const offMessage = this.normalR230522.on('message', (data) => this.emit('message', data));
    this.#off = () => {
      offStateChanged();
      offConnected();
      offMessage();
      this.#off = undefined;
      this.emit('stateChanged', 'closed');
    };
    if (this.state !== stateBeforeConnect) {
      this.emit('stateChanged', this.state);
    }
    this.normalR230522.connect();
  }

  close(): void {
    this.#off?.();
    this.normalR230522.close();
  }

  // oxlint-disable-next-line @typescript-eslint/no-explicit-any
  send(data: any): void {
    if (data.s) {
      if ('currencies' in data.s) return (this.currencies = data.s.currencies);
      if ('symbol' in data.s) return (this.symbol = data.s.symbol);
      if ('tickers' in data.s) return (this.tickers = data.s.tickers);
      if ('intervalData' in data.s) return (this.intervalData = data.s.intervalData);
    }
    this.normalR230522.send(data);
  }

  get debugLabel(): string {
    return this.socketName;
  }

  protected flushQueuedMessages(): void {}
  protected clearQueuedMessages(): void {}
}

interface Subscription {
  rows: string[]; // "*" for all rows, or specific row IDs
}

interface Handle {
  set(rows?: string[] | null): void;
}

class SubscriptionManager {
  #batchUpdater: BatchUpdater;
  #subscriptions: Record<string, Set<Subscription>> = {};

  constructor(private updateFn: (subscriptions: Record<string, Subscription>) => void) {
    this.#batchUpdater = new BatchUpdater((topics) => this.updateFn(this.getSubscriptions(topics)));
  }

  get everyTopics() {
    return Object.keys(this.#subscriptions);
  }

  getHandle(topics: string[], initialRows: string[] = []): Handle {
    const subscriptions: Record<string, Subscription> = {};
    for (const topic of topics) {
      const subscription = (subscriptions[topic] = { rows: initialRows });
      this.subscribe(topic, subscription);
    }
    return {
      set: (rows) => {
        for (const topic of topics) {
          const subscription = subscriptions[topic];
          subscription.rows = rows ?? [];
          this.#batchUpdater.touch(topic);
        }
      },
    };
  }

  subscribe(topic: string, subscription: Subscription): () => void {
    this.#subscriptions[topic] ??= new Set();
    this.#subscriptions[topic].add(subscription);
    this.#batchUpdater.touch(topic);
    return () => {
      this.#subscriptions[topic]?.delete(subscription);
      this.#batchUpdater.touch(topic);
    };
  }

  getSubscriptions(topics: string[] = this.everyTopics): Record<string, Subscription> {
    const result: Record<string, Set<string>> = {};
    for (const topic of topics) {
      const resultTopicRows = (result[topic] ??= new Set());
      for (const { rows } of this.#subscriptions[topic] ?? []) {
        for (const row of rows) resultTopicRows.add(row);
      }
    }
    return Object.fromEntries(Object.entries(result).map(([topic, rows]) => [topic, { rows: Array.from(rows) }]));
  }
}

class BatchUpdater {
  #touchedTopics: Set<string> = new Set();
  #timeoutId: NodeJS.Timeout | undefined;

  constructor(private updateFn: (topics: string[]) => void) {}

  touch(topic: string): void {
    this.#touchedTopics.add(topic);
    if (this.#timeoutId) return;
    this.#timeoutId = setTimeout(() => {
      const touchedTopics = Array.from(this.#touchedTopics);
      this.#touchedTopics.clear();
      this.#timeoutId = undefined;
      this.updateFn(touchedTopics);
    });
  }
}
